import { AxiosError } from 'axios';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useHistory, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import _ from 'underscore';
import { getScenarioStatus } from 'views/bluviu-nutrition/api/get-scenario-status';
import { getR2Scenario } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-get';
import { getR2ScenarioSites } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-sites-get';
import { getR2Scenarios, getScenariosCageMappingData } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenarios-get';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { ScenarioCageData } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-cage-data.type';
import { ScenarioListItem } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-list-item.type';
import { ScenarioSubTab } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-sub-tab.enum';
import { BluViuNutritionSite } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-site-type';
import { Scenario } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario.type';
import { ScenarioState } from 'views/bluviu-nutrition/types/scenario-state.enum';

type State = {
  r2ScenariosQuery: UseQueryResult<ScenarioListItem[], AxiosError<unknown, any>>;
  r2ScenarioQuery: UseQueryResult<Scenario, AxiosError<unknown, any>>;
  r2ScenarioSitesQuery: UseQueryResult<BluViuNutritionSite[], AxiosError<unknown, any>>;
  r2ScenarioStatusQuery: UseQueryResult<{ scenarioIsRunning: boolean }, AxiosError<unknown, any>>;
  r2ScenarioCageMappingDataQuery: UseQueryResult<ScenarioCageData[], AxiosError<unknown, any>>;
};

const ScenarioContext = React.createContext<State | undefined>(undefined);

type ScenarioProviderProps = {
  children: React.ReactNode;
};

const REFETCH_STATUS_INTERVAL = 3000; // 3 seconds

const ScenarioProvider: React.FC<ScenarioProviderProps> = ({ children }) => {
  const params = useParams<R2ScenarioParams>();
  const history = useHistory();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();

  const [scenarioStatusRefetchInterval, setScenarioStatusRefetchInterval] = useState<number | false | undefined>(false);
  const [scenarioRefetchInterval, setScenarioRefetchInterval] = useState<number | false | undefined>(false);
  const [shouldNotifyResultsReady, setShouldNotifyResultsReady] = useState(false);
  const previousScenarioRunning = useRef<{ key: string; isRunning: boolean }>();

  const {
    account: { company },
  } = useAccountState();

  const r2ScenariosQuery = useGet(
    ['getR2Scenarios', params.globalSiteId],
    ({ queryKey }) => getR2Scenarios(String(queryKey[1])),
    {
      enabled: !!params.globalSiteId,
      staleTime: 1 * 60 * 1000, // 1 minute stale time to prevent refetching when navigating between scenarios list and scenario overview. This is a temporary fix until we can implement better routing to prevent context overlap
    }
  );

  const r2ScenarioQuery = useGet(
    ['getR2Scenario', params.globalSiteId, params.scenarioId],
    ({ queryKey }) => getR2Scenario(String(queryKey[2]), String(queryKey[1])),
    {
      enabled: !!params.scenarioId,
      refetchInterval: scenarioRefetchInterval,
      select: (data) => ({
        ...data,
        cageData: _.sortBy(data.cageData, 'stockingDate'),
      }),
    }
  );

  const r2ScenarioSitesQuery = useGet(
    ['getR2ScenarioSites', company?.companyId],
    ({ queryKey }) => getR2ScenarioSites(String(queryKey[1])),
    {
      select: (data) => _.sortBy(data, 'siteName'),
      enabled: !!company?.companyId,
      staleTime: 1 * 60 * 1000, // 1 minute stale time since sites don't change often
    }
  );

  const r2ScenarioStatusQuery = useGet(
    ['getR2ScenarioStatus', params.globalSiteId, params.scenarioId],
    ({ queryKey }) => getScenarioStatus(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!scenarioStatusRefetchInterval,
      refetchInterval: scenarioStatusRefetchInterval,
    }
  );
  useEffect(() => {
    const scenario = r2ScenarioQuery.data;
    if (!scenario) return;

    if (scenario.state === ScenarioState.SUBMITTED) {
      setScenarioStatusRefetchInterval(REFETCH_STATUS_INTERVAL);
    }

    if (shouldNotifyResultsReady && scenario.state === ScenarioState.SCENARIO_RESULT_SENT) {
      setShouldNotifyResultsReady(false);
      setScenarioRefetchInterval(false);
      queryClient.invalidateQueries({
        queryKey: ['getR2Scenarios', params.globalSiteId],
        exact: true,
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: {
          msg: intl.formatMessage({ id: 'snackbar.success.scenarioResultsReady' }),
          action: {
            label: intl.formatMessage({ id: 'snackbar.success.viewResults' }),
            onClick: () => history.push(`/bluviu-nutrition/${params.tab}/${params.globalSiteId}/${params.scenarioId}/${ScenarioSubTab.RESULTS}`),
          },
        },
      });
    }

    if (shouldNotifyResultsReady && scenario.state === ScenarioState.FAILED) {
      setShouldNotifyResultsReady(false);
      setScenarioRefetchInterval(false);
    }
  }, [history, intl, r2ScenarioQuery.data, params.globalSiteId, params.scenarioId, params.tab, queryClient, shouldNotifyResultsReady, snackbarDispatch]);

  useEffect(() => {
    const status = r2ScenarioStatusQuery.data;
    if (!status) return;

    const statusKey = `${params.globalSiteId}:${params.scenarioId}`;
    const previousStatus = previousScenarioRunning.current;

    setScenarioStatusRefetchInterval(status.scenarioIsRunning ? REFETCH_STATUS_INTERVAL : false);

    if (previousStatus?.key === statusKey && previousStatus.isRunning && !status.scenarioIsRunning) {
      setShouldNotifyResultsReady(true);
      setScenarioRefetchInterval(REFETCH_STATUS_INTERVAL);
      queryClient.invalidateQueries({
        queryKey: ['getR2Scenario', params.globalSiteId, params.scenarioId],
      });
      queryClient.invalidateQueries({
        queryKey: ['getR2Scenarios', params.globalSiteId],
        exact: true,
      });
    }

    previousScenarioRunning.current = { key: statusKey, isRunning: status.scenarioIsRunning };
  }, [r2ScenarioStatusQuery.data, params.globalSiteId, params.scenarioId, queryClient]);

  const r2ScenarioCageMappingDataQuery = useGet(
    ['getR2ScenarioCageMappingData', params.scenarioId, params.globalSiteId],
    ({ queryKey }) => getScenariosCageMappingData(params.scenarioId, params.globalSiteId),
    {
      enabled: !!params.scenarioId && !!params.globalSiteId,
    }
  );

  return (
    <ScenarioContext.Provider
      value={{
        r2ScenarioQuery,
        r2ScenariosQuery,
        r2ScenarioSitesQuery,
        r2ScenarioStatusQuery,
        r2ScenarioCageMappingDataQuery,
      }}>
      {children}
    </ScenarioContext.Provider>
  );
};

function useR2ScenarioState() {
  const context = useContext(ScenarioContext);
  if (context === undefined) {
    throw new Error('useR2ScenarioState must be used within a ScenarioProvider');
  }
  return context;
}

export { ScenarioProvider, useR2ScenarioState };
